import Container from 'widget/components/Container';
import ExternalLink from 'widget/components/ExternalLink';
import globalData from 'modules/Analytics/helpers/globalData';
import helpers from 'widget/PlaylistRadio/helpers';
import IHeartLink from 'widget/components/IHeartLink';
import InfoCard from 'widget/components/InfoCard';
import MainContent from 'widget/components/MainContent';
import PLAYED_FROM from 'modules/Analytics/constants/playedFrom';
import Player from 'widget/contexts/Player';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import PlaylistRadioPlayer from 'widget/PlaylistRadio/PlaylistRadioPlayer';
import ShouldShow from 'components/ShouldShow';
import Tracks from 'widget/PlaylistRadio/Tracks';
import useModal from 'widget/components/Modal/hooks/useModal';
import useMount from 'hooks/useMount';
import useTrackPageViewOnMount from 'widget/hooks/useTrackPageViewOnMount';
import { ComponentProps } from 'widget/PlaylistRadio/types';
import { formatDuration } from 'views/Playlist/Hero/PlaylistHeroDescription';
import { useContext, useState } from 'react';
import { useTranslate } from 'widget/hooks';

function PlaylistRadio({
  analytics,
  author,
  autoplay,
  description,
  duration,
  embedUrl,
  followUrl,
  imageUrl,
  playbackSupportedCountry,
  name = '',
  reportingKey,
  playlistRadioUrl,
  tracks,
  trackIds,
  websiteUrl,
}: ComponentProps) {
  const translate = useTranslate();
  const { getCurrentlyPlaying, playerState } = useContext(Player.Context);
  const {
    artistName = null,
    isError,
    songName = null,
    trackImage = null,
    trackId,
  } = getCurrentlyPlaying() || {};
  const [hasPlayed] = useState(false);

  useTrackPageViewOnMount();

  useMount(() => {
    if (analytics) {
      analytics.set(globalData({ volume: 100 }));
      analytics.trackPageView?.({
        contentFrame: 'responsive_widget',
        id: `playlist_radio|${reportingKey}`,
        name,
        pageName: 'playlist_radio_profile',
      });
    }
  });

  const [, toggleInfoModal] = useModal(
    <InfoCard body={description} title={name} url={playlistRadioUrl} />,
  );

  const playlistName = (
    <ExternalLink data-test="title-link" href={playlistRadioUrl}>
      {name}
    </ExternalLink>
  );

  const interacted =
    playerState === PlayerState.Playing || playerState === PlayerState.Paused;

  return (
    <Container>
      <ShouldShow
        shouldShow={
          !Object.values(PlayerErrorState).includes(
            playerState as PlayerErrorState,
          )
        }
      >
        <MainContent
          description={description}
          hasPlayed={[
            PlayerState.Playing,
            PlayerState.Paused,
            PlayerState.Buffering,
          ].includes(playerState as PlayerState)}
          icons={[<IHeartLink href={websiteUrl} key="iheartlink" />]}
          infoHeading={`By ${author} • ${formatDuration(duration)}`}
          subHeading={description ?? author}
          title={interacted ? songName : playlistName}
          toggleInfoModal={toggleInfoModal}
        />
      </ShouldShow>

      <PlaylistRadioPlayer
        analytics={analytics}
        analyticsData={{
          name,
          playedFrom: PLAYED_FROM.RESP_WIDGET_PLAYER_PLAY,
          subName: reportingKey,
          subId: trackId,
          songName,
        }}
        autoplay={autoplay}
        description={description}
        embedUrl={embedUrl}
        followUrl={followUrl}
        hasPlayed={hasPlayed}
        imageUrl={imageUrl}
        isError={isError}
        metadata={{
          artist: artistName,
          description,
          mainImage: imageUrl,
          title: songName,
          trackImage,
        }}
        playbackSupportedCountry={playbackSupportedCountry}
        playlistRadioUrl={playlistRadioUrl}
        positionBottom={false}
        reportingKey={reportingKey}
        shareText={translate(`Share this {name} playlist`, { name })}
        shareTitle={name}
        title={name}
        trackIds={trackIds}
      />
      <Tracks
        cursorPointer={false}
        playlistRadioUrl={playlistRadioUrl}
        tracks={tracks}
      />
    </Container>
  );
}

PlaylistRadio.getInitialProps = helpers.getInitialProps;

export default PlaylistRadio;
